import { Fragment, useState } from "react";
import { useDraftRows, UnsavedBar } from "@/components/useDraftRows";
import { Plus, Trash2, Save, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Cell } from "@/components/EditableTable";

const MAIN = [["code", "Code", "14%"], ["name", "Name", "25%"], ["family", "Material family", "17%"], ["form", "Format", "10%"], ["cost", "Cost", "15%"]];
const DETAIL = ["category", "unit", "currency", "pack_qty", "gsm", "size", "width", "length", "dim_unit"];
const DOT = { offset: "#13294B", digital: "#45BFB4", large_format: "#F59E0B", label: "#E11D48", souvenir: "#8B5CF6" };

function Fld({ col, r, i, set, testId, label }) {
  return (
    <div className="min-w-0 [&_input]:min-w-0 [&_input]:px-2 [&_select]:pl-1.5 [&_select]:pr-6">
      {label && <span className="block text-[11px] text-slate-500">{col.label}</span>}
      <Cell col={col} row={r} value={r[col.key]} onChange={(v) => set(i, col.key, v)} testId={`${testId}-${i}-${col.key}`} />
      {col.suffix && !col.disabledIf?.(r) && <span className="block break-words text-[10px] leading-tight text-slate-400" data-testid={`${testId}-${i}-${col.key}-unit`}>{col.suffix(r)}</span>}
    </div>
  );
}

function LineSummary({ lines, testId, onClick, noun = "line" }) {
  const v = lines || [];
  return (
    <button type="button" onClick={onClick} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-[#13294B]" data-testid={testId} title={v.map((s) => s.replace(/_/g, " ")).join(", ")}>
      <span className="flex -space-x-0.5">{v.slice(0, 5).map((s) => <span key={s} className="h-2.5 w-2.5 rounded-full ring-1 ring-white" style={{ background: DOT[s] || "#94A3B8" }} />)}</span>
      <span className="whitespace-nowrap">{v.length} {v.length === 1 ? noun : `${noun}s`}</span>
    </button>
  );
}

function Detail({ r, i, set, testId, by, detail, chips, chipsLabel }) {
  return (
    <div className="space-y-3 bg-slate-50 px-4 py-3" data-testid={`${testId}-${i}-detail`}>
      {detail.length > 0 && <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">{detail.map((k) => by[k] && <Fld key={k} col={by[k]} r={r} i={i} set={set} testId={testId} label />)}</div>}
      <div><span className="mb-1 block text-[11px] text-slate-500">{chipsLabel}</span><Fld col={by[chips]} r={r} i={i} set={set} testId={testId} /></div>
    </div>
  );
}

/** Materials master: compact identify-and-price row; everything else in an inline detail panel per row. */
export function MaterialsTable({ columns, rows, onSave, testId, readOnly, blank = {}, filter, onRemove, onDiscard, main = MAIN, detail = DETAIL, chips = "service_lines", chipsLabel = "Service lines", noun = "line" }) {
  const by = Object.fromEntries(columns.map((c) => [c.key, c]));
  const { data, setData, dirty, discard } = useDraftRows(rows, testId, undefined, onDiscard);
  const [open, setOpen] = useState({});
  const isOpen = (r, i) => open[i] ?? !!r._draft;
  const set = (i, k, v) => setData((d) => d.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const toggle = (i) => setOpen({ ...open, [i]: !isOpen(data[i] || {}, i) });
  const remove = async (r, i) => { if (!onRemove || (await onRemove(r)) === true) setData((d) => d.filter((_, j) => j !== i)); };
  const shown = filter ? data.filter((r) => filter(r)).length : data.length;
  const n = main.length + 3;
  return (
    <div data-testid={testId}>
      <UnsavedBar dirty={dirty} onDiscard={discard} testId={testId} />
      <div className="overflow-x-auto"><table className="po-table w-full min-w-[760px] table-fixed lg:min-w-0" data-testid={`${testId}-table`}>
        <colgroup><col style={{ width: "4%" }} />{main.map(([k, , w]) => <col key={k} style={{ width: w }} />)}<col style={{ width: "10%" }} /><col style={{ width: "5%" }} /></colgroup>
        <thead><tr><th />{main.map(([k, l]) => <th key={k} className="!whitespace-normal">{l}</th>)}<th className="!whitespace-normal">{chipsLabel}</th><th /></tr></thead>
        <tbody>
          {data.map((r, i) => (!filter || filter(r)) && (
            <Fragment key={i}>
              <tr data-testid={`${testId}-row-${i}`}>
                <td className="!px-1.5 !py-1.5 align-top"><button type="button" aria-expanded={isOpen(r, i)} title="Show details" data-testid={`${testId}-${i}-expand`} onClick={() => toggle(i)} className="mt-1.5 text-slate-400 hover:text-[#389F95]">
                  <ChevronRight className={`h-4 w-4 transition-transform ${isOpen(r, i) ? "rotate-90" : ""}`} /></button></td>
                {main.map(([k]) => <td key={k} className="!px-1.5 !py-1.5 align-top"><Fld col={by[k]} r={r} i={i} set={set} testId={testId} /></td>)}
                <td className="!px-1.5 !py-1.5 align-top"><div className="mt-2"><LineSummary lines={r[chips]} noun={noun} testId={`${testId}-${i}-lines-summary`} onClick={() => toggle(i)} /></div></td>
                <td className="!px-1.5 !py-1.5 align-top">{!readOnly && <button type="button" data-testid={`${testId}-remove-${i}`} className="mt-1.5 text-slate-400 hover:text-rose-600" onClick={() => remove(r, i)}><Trash2 className="h-4 w-4" /></button>}</td>
              </tr>
              {isOpen(r, i) && <tr><td colSpan={n} className="!p-0"><Detail r={r} i={i} set={set} testId={testId} by={by} detail={detail} chips={chips} chipsLabel={chipsLabel} /></td></tr>}
            </Fragment>
          ))}
          {filter && shown === 0 && <tr><td colSpan={n} className="py-6 text-center text-sm text-slate-400" data-testid={`${testId}-no-match`}>No rows match the filters.</td></tr>}
        </tbody>
      </table></div>
      {!readOnly && (
        <div className="flex gap-2 border-t border-slate-100 p-3">
          <Button size="sm" variant="outline" data-testid={`${testId}-add`} onClick={() => { setData([...data, { ...blank }]); setOpen({ ...open, [data.length]: true }); }}><Plus className="mr-1 h-4 w-4" />Add row</Button>
          <Button size="sm" data-testid={`${testId}-save`} onClick={() => onSave(data.map(({ _draft, ...r }) => r))} className="bg-[#13294B] hover:bg-[#1c3a66]"><Save className="mr-1 h-4 w-4" />Save changes</Button>
        </div>
      )}
    </div>
  );
}
